import { useEffect, useMemo, useRef, useState } from "react";
import { Camera, ChevronRight, CircleCheck, FileImage, FileUp, Image, Settings } from "lucide-react";
import { useAppStore } from "../state/appStore";
import { createDemoProject } from "../core/pattern";
import { projectRepository } from "../core/database";
import { importProjectBundle } from "../core/exporters";
import { prepareOfflineRecognition, subscribeOfflinePack, type OfflinePackStatus } from "../core/offline";
import { EmptyState, IconButton, ProgressBar, Sheet } from "../components/ui";

interface HomeScreenProps { onImport: (file: File) => void; }

let demoSeedPromise: Promise<void> | null = null;

function isStandalone(): boolean {
  return window.matchMedia("(display-mode: standalone)").matches || navigator.standalone === true;
}

function Preview({ blob }: { blob?: Blob }) {
  const url = useMemo(() => blob ? URL.createObjectURL(blob) : null, [blob]);
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);
  return url ? <img src={url} alt="项目图纸缩略图" /> : <div className="project-placeholder" aria-hidden="true" />;
}

export function HomeScreen({ onImport }: HomeScreenProps) {
  const { summaries, loadProjects, openProject, setScreen, setToast } = useAppStore();
  const photoInput = useRef<HTMLInputElement>(null);
  const cameraInput = useRef<HTMLInputElement>(null);
  const backupInput = useRef<HTMLInputElement>(null);
  const [showRequirements, setShowRequirements] = useState(false);
  const [showInstall, setShowInstall] = useState(false);
  const [offlinePack, setOfflinePack] = useState<OfflinePackStatus>({ state: "idle", completed: 0, total: 20 });

  useEffect(() => { void loadProjects(); }, [loadProjects]);
  useEffect(() => {
    const unsubscribe = subscribeOfflinePack(setOfflinePack);
    void prepareOfflineRecognition().catch(() => undefined);
    return unsubscribe;
  }, []);
  useEffect(() => {
    if (!new URLSearchParams(location.search).has("demo")) return;
    demoSeedPromise ??= (async () => {
      const items = await projectRepository.list();
      if (!items.length) {
        await projectRepository.create(createDemoProject());
        await loadProjects();
      }
    })();
  }, [loadProjects]);

  async function restore(file?: File) {
    if (!file) return;
    try {
      const restored = await importProjectBundle(file);
      await projectRepository.create(restored.project, restored.source, restored.preview);
      await loadProjects();
      setToast("项目已恢复");
    } catch (error) { setToast(error instanceof Error ? error.message : "项目恢复失败"); }
    backupInput.current!.value = "";
  }

  return (
    <main className="screen home-screen">
      <header className="app-header"><span /><h1>拼豆助手</h1><IconButton label="设置" onClick={() => setScreen("settings")}><Settings /></IconButton></header>
      <section className="import-hero">
        <div className="grid-watermark" aria-hidden="true" />
        <h2>开始一张新图纸</h2>
        <p>导入带网格与色号的拼豆图纸，识别过程仅在本机完成。</p>
        <button className="primary import-button" onClick={() => photoInput.current?.click()}><Image />从相册导入</button>
        <button className="secondary import-button" onClick={() => cameraInput.current?.click()}><Camera />拍照或选择文件</button>
        <input ref={photoInput} hidden type="file" accept="image/png,image/jpeg,image/webp,image/heic,image/heif" onChange={(event) => event.target.files?.[0] && onImport(event.target.files[0])} />
        <input ref={cameraInput} hidden type="file" accept="image/*" capture="environment" onChange={(event) => event.target.files?.[0] && onImport(event.target.files[0])} />
      </section>

      <section className="projects-section">
        <div className="section-heading"><h2>最近项目</h2><span>{summaries.length ? `${summaries.length} 个项目` : "全部项目"}</span></div>
        {summaries.length ? summaries.map((project) => (
          <button key={project.id} className="project-row" onClick={() => void openProject(project.id)}>
            <div className="project-thumb"><Preview blob={project.previewBlob} /></div>
            <div className="project-copy">
              <strong>{project.name}</strong>
              <span>{project.width} × {project.height} · {project.colorCount} 色 · {project.total} 颗</span>
              <span>已完成 <b>{project.completed}</b></span>
              <ProgressBar value={project.completed} max={project.total} />
            </div>
            <ChevronRight aria-hidden="true" />
          </button>
        )) : (
          <EmptyState icon={<FileImage />} title="还没有图纸" description="导入一张规则网格图纸，几秒后即可开始制作。" />
        )}
        <button className="list-action" onClick={() => setShowRequirements(true)}><FileImage /><span>导入要求</span><ChevronRight /></button>
        <button className="list-action" onClick={() => backupInput.current?.click()}><FileUp /><span>恢复 .pindou 项目</span><ChevronRight /></button>
        <input ref={backupInput} hidden type="file" accept=".pindou,application/vnd.pindou+zip,application/zip" onChange={(event) => void restore(event.target.files?.[0])} />
      </section>

      {!isStandalone() && <button className="install-hint" onClick={() => setShowInstall(true)}>添加到主屏幕，像 App 一样使用</button>}
      <div className={`offline-status ${offlinePack.state}`} role="status" aria-live="polite"><CircleCheck />{offlinePack.state === "ready" ? "可离线使用" : offlinePack.state === "error" ? "离线包未完成，联网后点此重试" : `正在准备离线识别 ${Math.round(offlinePack.completed / Math.max(offlinePack.total, 1) * 100)}%`}{offlinePack.state === "error" && <button onClick={() => void prepareOfflineRecognition().catch(() => undefined)}>重试</button>}</div>

      {showRequirements && <Sheet title="导入要求" onClose={() => setShowRequirements(false)}>
        <ul className="requirement-list">
          <li><b>推荐：</b>原始导出截图或高清长图。</li>
          <li>网格必须完整，横竖线清楚，四个边角都在图片内。</li>
          <li>保留标题尺寸、坐标栏和底部色号图例，可显著提高准确率。</li>
          <li>照片请避免反光、阴影、遮挡和过度倾斜。</li>
          <li>单张不超过 20MB，最大图纸 200 × 200。</li>
        </ul>
      </Sheet>}
      {showInstall && <Sheet title="安装到 iPhone" onClose={() => setShowInstall(false)}>
        <ol className="install-steps"><li>使用 Safari 打开当前网址。</li><li>点击底部“分享”按钮。</li><li>选择“添加到主屏幕”。</li><li>开启“作为网页 App 打开”，再点“添加”。</li></ol>
      </Sheet>}
    </main>
  );
}
